import React, { useRef, useState } from "react";
import axios from "axios";

const API_URL = "https://sdi909uolk.execute-api.ap-south-1.amazonaws.com";
const MAX_FILE_SIZE = 15 * 1024 * 1024;
const ACCEPTED_TYPES = ["application/pdf", "image/jpeg", "image/png"];

function StatementUpload({ token, onDone }) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [source, setSource] = useState("Bank statement");
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState("");

  const chooseFile = (selectedFile) => {
    setMessage("");
    if (!selectedFile) return;
    if (!ACCEPTED_TYPES.includes(selectedFile.type)) {
      setMessage("Please choose a PDF, JPG, or PNG file.");
      return;
    }
    if (selectedFile.size > MAX_FILE_SIZE) {
      setMessage("The file must be smaller than 15 MB.");
      return;
    }
    setFile(selectedFile);
  };

  const upload = async () => {
    if (!file) {
      setMessage("Choose a statement or payment screenshot first.");
      return;
    }
    setUploading(true);
    setMessage("");
    try {
      // The API must identify the signed-in user from the token. It must not
      // accept an S3 path or user id from this browser.
      const presign = await axios.post(
        `${API_URL}/uploads/presign`,
        { file_name: file.name, content_type: file.type, source },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      await axios.put(presign.data.upload_url, file, {
        headers: { "Content-Type": file.type },
      });

      // Store upload metadata and trigger statement processing, if enabled.
      await axios.post(
        `${API_URL}/uploads/complete`,
        { upload_id: presign.data.upload_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage("Upload saved. Your statement is ready for processing.");
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
    } catch (error) {
      console.error("Statement upload failed:", error);
      setMessage("Upload could not be completed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="subpage">
      <button className="btn" onClick={onDone}>← Back to dashboard</button>
      <h2 className="subpage-title page-title-spaced">Upload a statement</h2>
      <p className="page-intro">Add a bank statement, PhonePe / GPay receipt, or payment screenshot. It stays private to your signed-in profile.</p>

      <div className="field-group">
        <label className="field-label">Document type</label>
        <div className="chip-row">
          {["Bank statement", "PhonePe", "Google Pay", "Other"].map((item) => (
            <button type="button" key={item} className={`chip-btn ${source === item ? "active" : ""}`} onClick={() => setSource(item)}>{item}</button>
          ))}
        </div>
      </div>

      <button className="upload-dropzone" type="button" onClick={() => inputRef.current?.click()}>
        <span className="upload-icon">↑</span>
        <strong>{file ? file.name : "Choose a statement or screenshot"}</strong>
        <span>PDF, JPG, or PNG · maximum 15 MB</span>
      </button>
      <input ref={inputRef} className="visually-hidden" type="file" accept=".pdf,image/jpeg,image/png" onChange={(event) => chooseFile(event.target.files?.[0])} />

      {message && <p className={`upload-message ${message.startsWith("Upload saved") ? "success" : ""}`}>{message}</p>}
      <div className="form-actions">
        <button className="btn btn-dark" type="button" onClick={upload} disabled={uploading}>{uploading ? "Uploading..." : "Upload securely"}</button>
        <button className="btn" type="button" onClick={onDone}>Cancel</button>
      </div>
    </div>
  );
}

export default StatementUpload;
